import React, { useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslate } from '../TranslationText';
import TranslationsKeys from '../../TranslationsKeys';

/**
 * Clean file upload with React Hook Form integration.
 * Built from scratch — no SimpleUpload/NetaqInput dependency.
 *
 * @param {string} name - Form field name
 * @param {string} label - Label text
 * @param {boolean} required - Show required asterisk
 * @param {boolean} disabled - Disable upload
 * @param {object} rules - Validation rules
 * @param {string} accept - Accepted MIME types (default: 'image/*,application/pdf')
 * @param {boolean} isMulti - Allow multiple files
 * @param {number} maxSizeMB - Max file size in MB (default: 10)
 * @param {boolean} hideLabel - Hide the label
 */
export default function FormFileUpload({
    name,
    label,
    required = false,
    disabled = false,
    rules,
    accept = 'image/*,application/pdf',
    isMulti = false,
    maxSizeMB = 10,
    hideLabel = false,
    noValidate = false,
}) {
    const { control, formState: { errors }, setError, clearErrors } = useFormContext();
    const { translate } = useTranslate();
    const inputRef = useRef(null);

    const validationRules = {
        ...(required && !noValidate && {
            required: translate(TranslationsKeys.REQUIRED_FIELD, 'Required Field'),
        }),
        ...rules,
    };

    const handleFileSelect = (e, fieldOnChange, currentFiles) => {
        if (!e.target.files || e.target.files.length === 0) return;

        const maxBytes = maxSizeMB * 1024 * 1024;
        const newFiles = [];

        for (let i = 0; i < e.target.files.length; i++) {
            const file = e.target.files[i];

            if (file.size > maxBytes) {
                setError(name, {
                    type: 'manual',
                    message: translate(TranslationsKeys.FILE_SIZE_LIMIT, 'File size 10MB limit.'),
                });
                continue;
            }

            const allowedMimeTypes = ['image/', 'application/pdf'];
            if (!allowedMimeTypes.some((type) => file.type.startsWith(type))) {
                setError(name, {
                    type: 'manual',
                    message: translate(TranslationsKeys.INVALID_FILE_TYPE, 'Only images and PDFs allowed.'),
                });
                continue;
            }

            newFiles.push({
                file,
                name: file.name,
                preview: URL.createObjectURL(file),
            });
        }

        if (newFiles.length > 0) {
            clearErrors(name);
            const result = isMulti ? [...(currentFiles || []), ...newFiles] : newFiles;
            fieldOnChange(result);
        }

        // Reset input so same file can be selected again
        if (inputRef.current) inputRef.current.value = '';
    };

    const removeFile = (index, fieldOnChange, currentFiles) => {
        const updated = [...(currentFiles || [])];
        // Revoke object URL to prevent memory leaks
        if (updated[index]?.preview) URL.revokeObjectURL(updated[index].preview);
        updated.splice(index, 1);
        fieldOnChange(updated.length > 0 ? updated : null);
    };

    const openFile = (file) => {
        if (file.preview) {
            window.open(file.preview, '_blank', 'noopener,noreferrer');
        } else if (file.url) {
            window.open(file.url, '_blank', 'noopener,noreferrer');
        }
    };

    const fieldError = name.split('.').reduce((obj, key) => obj?.[key], errors);

    return (
        <div className="mb-2">
            {!hideLabel && label && (
                <label htmlFor={name} className="capatalize_text">
                    {label}{' '}
                    {required && <i style={{ color: 'red', margin: 'auto 2px' }}>*</i>}
                </label>
            )}

            <Controller
                name={name}
                control={control}
                rules={validationRules}
                render={({ field: { onChange, value, ref: fieldRef } }) => {
                    const files = value || [];
                    const displayName = Array.isArray(files)
                        ? files.map((f) => f.name).join(', ')
                        : '';

                    return (
                        <>
                            <div className="input-group row mx-0 rtl">
                                <input
                                    type="text"
                                    ref={fieldRef}
                                    className="form-control col-lg-7 col-md-12 col-sm-12 col-12"
                                    placeholder={translate(TranslationsKeys.NO_FILE_SELECTED, 'No file selected')}
                                    style={{ cursor: 'pointer', fontSize: 13 }}
                                    value={displayName}
                                    readOnly
                                    onClick={() => {
                                        if (files.length === 1) openFile(files[0]);
                                    }}
                                />
                                <div
                                    className="input-group-text btn-dark text-center btn col-lg-5 col-md-12 col-sm-12 col-12"
                                    style={{
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        opacity: disabled ? 0.65 : 1,
                                        fontSize: 13,
                                        zIndex: 1,
                                    }}
                                    onClick={() => {
                                        if (!disabled && inputRef.current) inputRef.current.click();
                                    }}
                                >
                                    {translate(TranslationsKeys.UPLOAD_FILE, 'Upload File')}
                                </div>
                            </div>

                            <input
                                ref={inputRef}
                                type="file"
                                multiple={isMulti}
                                accept={accept}
                                style={{ display: 'none' }}
                                onChange={(e) => handleFileSelect(e, onChange, files)}
                            />

                            {/* File previews */}
                            {Array.isArray(files) && files.length > 0 && (
                                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {files.map((f, idx) => (
                                        <div
                                            key={idx}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                padding: '2px 8px',
                                                background: '#f0f0f0',
                                                borderRadius: 4,
                                                fontSize: 12,
                                            }}
                                        >
                                            <span
                                                style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1B6CB5' }}
                                                onClick={() => openFile(f)}
                                            >
                                                {f.name}
                                            </span>
                                            {!disabled && (
                                                <span
                                                    style={{ cursor: 'pointer', color: 'red', fontWeight: 'bold', marginLeft: 4 }}
                                                    onClick={() => removeFile(idx, onChange, files)}
                                                    title={translate(TranslationsKeys.REMOVE, 'Remove')}
                                                >
                                                    ×
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    );
                }}
            />

            {fieldError && (
                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                    {fieldError.message || translate(TranslationsKeys.REQUIRED_FIELD, 'Required Field')}
                </div>
            )}
        </div>
    );
}
